import { useState, useEffect } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Search, UserPlus, Mail, ShieldCheck } from 'lucide-react';

interface User {
  id: string;
  user_id: string;
  email: string;
  is_active: boolean;
  agency_id: string | null;
  agency_name: string | null;
  has_supervisor_role: boolean;
}

interface Agency {
  id: string;
  name: string;
  code: string;
}

interface PromoteToSupervisorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function PromoteToSupervisorDialog({ open, onOpenChange, onSuccess }: PromoteToSupervisorDialogProps) {
  const { user } = useAdminAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgency, setSelectedAgency] = useState<string>('');
  const [promotingUser, setPromotingUser] = useState<User | null>(null);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (open) {
      if (isSuperAdmin) {
        loadAgencies();
      } else if (user?.agency_id) {
        setSelectedAgency(user.agency_id);
      }
    }
  }, [open, isSuperAdmin, user?.agency_id]);

  useEffect(() => {
    if (selectedAgency) {
      loadUsers();
    }
  }, [selectedAgency]);

  const loadAgencies = async () => {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('id, name, code')
        .eq('is_approved', true)
        .order('name');

      if (error) throw error;
      setAgencies(data || []);
    } catch (error) {
      console.error('Error loading agencies:', error);
    }
  };

  const loadUsers = async () => {
    if (!selectedAgency) return;

    setLoadingUsers(true);
    try {
      // Get profiles for the selected agency
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, user_id, email, is_active, agency_id')
        .eq('agency_id', selectedAgency);

      if (profilesError) throw profilesError;

      // Get supervisor roles
      const { data: supervisorRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'supervisor');

      if (rolesError) throw rolesError;

      const supervisorUserIds = new Set(supervisorRoles?.map(r => r.user_id) || []);

      // Get agency name
      const agency = agencies.find(a => a.id === selectedAgency);

      const usersData: User[] = (profiles || []).map(p => ({
        id: p.id,
        user_id: p.user_id,
        email: p.email,
        is_active: p.is_active,
        agency_id: p.agency_id,
        agency_name: agency?.name || null,
        has_supervisor_role: supervisorUserIds.has(p.user_id),
      }));

      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
      toast({
        title: "Error",
        description: "Failed to load users",
        variant: "destructive",
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const handlePromote = async (targetUser: User) => {
    setPromotingUser(targetUser);
    setLoading(true);

    try {
      // Add supervisor role
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: targetUser.user_id,
          role: 'supervisor',
          assigned_by: user?.id,
        });

      if (roleError) throw roleError;

      // Log audit event
      await supabase.from('admin_audit_logs').insert({
        actor_user_id: user?.id,
        action: 'role_added',
        target_resource: 'user_roles',
        target_id: targetUser.user_id,
        metadata: {
          email: targetUser.email,
          role: 'supervisor',
          action: 'promote',
        },
      });

      toast({
        title: "User Promoted",
        description: `${targetUser.email} is now a supervisor.`,
      });

      // Update local state
      setUsers(prev => prev.map(u => 
        u.user_id === targetUser.user_id 
          ? { ...u, has_supervisor_role: true }
          : u
      ));

      onSuccess();
    } catch (error: any) {
      console.error('Error promoting user:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to promote user",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setPromotingUser(null);
    }
  };

  const filteredUsers = users.filter(u =>
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const eligibleUsers = filteredUsers.filter(u => !u.has_supervisor_role);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Promote User to Supervisor
          </DialogTitle>
          <DialogDescription>
            Select an existing user to grant them supervisor privileges.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Agency Selection (Super Admin only) */}
          {isSuperAdmin && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Agency</label>
              <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an agency..." />
                </SelectTrigger>
                <SelectContent>
                  {agencies.map(agency => (
                    <SelectItem key={agency.id} value={agency.id}>
                      {agency.name} ({agency.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Search */}
          {selectedAgency && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          )}

          {/* Users List */}
          <div className="max-h-[300px] overflow-y-auto space-y-2">
            {loadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !selectedAgency ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>Select an agency to see users</p>
              </div>
            ) : eligibleUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <ShieldCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>
                  {searchTerm 
                    ? 'No matching users found' 
                    : filteredUsers.length > 0 
                      ? 'All users are already supervisors'
                      : 'No users in this agency'
                  }
                </p>
              </div>
            ) : (
              eligibleUsers.map(u => (
                <div
                  key={u.user_id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-primary/10 text-primary text-sm">
                        {u.email.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm font-medium">{u.email}</span>
                      </div>
                      <Badge 
                        variant={u.is_active ? 'default' : 'secondary'}
                        className={`text-xs ${u.is_active 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                          : ''
                        }`}
                      >
                        {u.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handlePromote(u)}
                    disabled={loading && promotingUser?.user_id === u.user_id}
                  >
                    {loading && promotingUser?.user_id === u.user_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4 mr-1" />
                        Promote
                      </>
                    )}
                  </Button>
                </div>
              ))
            )}
          </div>

          {/* Already Supervisors */}
          {filteredUsers.filter(u => u.has_supervisor_role).length > 0 && (
            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground mb-2">Already Supervisors:</p>
              <div className="flex flex-wrap gap-1">
                {filteredUsers.filter(u => u.has_supervisor_role).map(u => (
                  <Badge key={u.user_id} variant="secondary" className="text-xs">
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    {u.email}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
